import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid2,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Service, Appointment, Employee } from '../../types';
import { formatServicePrice, servicesIncludeVariablePrice } from '../../utils/servicePrice';
import { createEmptyAppointment } from '../../types';

interface ServicePickerProps {
  services: Service[];
  handleAppointment: (appointment: Appointment | null) => void;
  selectedEmployee: Employee | null;
  eligibleEmployees?: Employee[];
  isAnyAvailableSelected?: boolean;
  isAnyAvailableBlockedByPaymentCompatibility?: boolean;
  selectedServicesList: Service[] | null;
  setSelectedServicesList: (selectedServicesList: Service[] | null) => void;
  currentAppointment: Appointment | null;
  resetDateTimeOnServiceChange?: boolean;
  showPrices?: boolean;
}

const ServicePicker: React.FC<ServicePickerProps> = ({
  services,
  handleAppointment,
  selectedEmployee,
  eligibleEmployees = [],
  isAnyAvailableSelected = false,
  isAnyAvailableBlockedByPaymentCompatibility = false,
  selectedServicesList,
  setSelectedServicesList,
  currentAppointment,
  resetDateTimeOnServiceChange = true,
  showPrices = true,
}) => {
  const theme = useTheme();
  const eligibleEmployeeIds = useMemo(() => new Set(eligibleEmployees.map((employee) => employee.id)), [eligibleEmployees]);
  const appointmentEmployeeId = isAnyAvailableSelected ? '' : selectedEmployee?.id || '';

  const filteredServices = services.filter(
    (service) => {
      if (!Array.isArray(service.employees) || service.employees.length === 0) {
        return false;
      }

      if (isAnyAvailableSelected) {
        return service.employees.some((employeeId) => eligibleEmployeeIds.has(employeeId));
      }

      return !!selectedEmployee && service.employees.includes(selectedEmployee.id);
    }
  );

  const isSelected = (service: Service) =>
    selectedServicesList?.some((selectedService) => selectedService.id === service.id);

  const handleServiceToggle = (service: Service) => {
    const updatedList = selectedServicesList ? [...selectedServicesList] : [];

    const index = updatedList.findIndex((selectedService) => selectedService.id === service.id);

    if (index !== -1) {
      updatedList.splice(index, 1); // remove
    } else {
      updatedList.push(service); // add
    }

    setSelectedServicesList(updatedList.length > 0 ? updatedList : null);

    if (updatedList.length > 0) {
      // "Price varies" services count as $0 here; staff set the final price later,
      // and the booking never shows a dollar total to the customer.
      const billingAmount = updatedList.reduce((sum, s) => sum + (s.priceVaries ? 0 : s.price), 0);
      const priceToConfirm = servicesIncludeVariablePrice(updatedList);
      const appointmentDuration = updatedList.reduce((sum, s) => sum + s.time, 0);
      const appointmentTitle = updatedList.map((s) => s.title).join(', ');
      const servicesList = updatedList.map((s) => ({
        serviceTitle: s.title,
        serviceTime: s.time,
        serviceAmount: s.priceVaries ? 0 : s.price,
        id: s.id,
        ...(s.priceVaries ? { priceVaries: true } : {}),
      }));
      // Keep answers already given for services that are still selected.
      const serviceAnswers = (currentAppointment?.serviceAnswers ?? [])
        .filter((answer) => updatedList.some((selected) => selected.id === answer.serviceId));

      handleAppointment(
        createEmptyAppointment({
          employeeID: appointmentEmployeeId,
          billingAmount,
          appointmentDuration,
          appointmentTitle,
          servicesList,
          ...(priceToConfirm ? { priceToConfirm: true } : {}),
          ...(serviceAnswers.length ? { serviceAnswers } : {}),
          notes: currentAppointment?.notes || '',
          appointmentDate: resetDateTimeOnServiceChange ? '' : currentAppointment?.appointmentDate || '',
          appointmentTime: resetDateTimeOnServiceChange ? '' : currentAppointment?.appointmentTime || '',
        })
      );
    } else {
      handleAppointment(
        createEmptyAppointment({
          employeeID: appointmentEmployeeId,
          appointmentDate: resetDateTimeOnServiceChange ? '' : currentAppointment?.appointmentDate || '',
          appointmentTime: resetDateTimeOnServiceChange ? '' : currentAppointment?.appointmentTime || '',
          notes: currentAppointment?.notes || '',
        })
      );
    }
  };

  // Group services by category
  const categoryMap = useMemo(() => {
    const grouped: { [category: string]: Service[] } = {};
    filteredServices.forEach((service) => {
      const cat = service.category?.trim() || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(service);
    });
    return grouped;
  }, [filteredServices]);

  const serviceCategoryMap = useMemo(() => {
    const map = new Map<string, string>();
    filteredServices.forEach((service) => {
      const cat = service.category?.trim() || 'Other';
      map.set(service.id, cat);
    });
    return map;
  }, [filteredServices]);

  const categoryNames = useMemo(() => {
    return Object.keys(categoryMap).sort((a, b) => {
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a.localeCompare(b); // alphabetical sort
    });
  }, [categoryMap]);

  const [expandedCategories, setExpandedCategories] = useState<string[]>(() =>
    categoryNames.filter((cat) => categoryMap[cat]?.some(isSelected))
  );

  useEffect(() => {
    setExpandedCategories((prev) => {
      const next = prev.filter((cat) => categoryNames.includes(cat));
      const isSame =
        next.length === prev.length && next.every((cat, idx) => cat === prev[idx]);
      return isSame ? prev : next;
    });
  }, [categoryNames]);

  const previouslySelectedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const currentSelectedIds = new Set(
      (selectedServicesList ?? []).map((service) => service.id)
    );
    const previousSelectedIds = previouslySelectedIdsRef.current;

    const newlySelectedIds: string[] = [];
    currentSelectedIds.forEach((id) => {
      if (!previousSelectedIds.has(id)) {
        newlySelectedIds.push(id);
      }
    });

    if (newlySelectedIds.length > 0) {
      setExpandedCategories((prev) => {
        const nextSet = new Set(prev);
        newlySelectedIds.forEach((id) => {
          const cat = serviceCategoryMap.get(id);
          if (cat) {
            nextSet.add(cat);
          }
        });
        const next = Array.from(nextSet);
        const isSame =
          next.length === prev.length && next.every((cat, idx) => cat === prev[idx]);
        return isSame ? prev : next;
      });
    }

    previouslySelectedIdsRef.current = currentSelectedIds;
  }, [selectedServicesList, serviceCategoryMap]);

  const handleAccordionChange =
    (cat: string) => (_event: React.SyntheticEvent, expanded: boolean) => {
      setExpandedCategories((prev) => {
        if (expanded) {
          if (prev.includes(cat)) return prev;
          return [...prev, cat];
        }
        return prev.filter((item) => item !== cat);
      });
    };

  return (
    <Box>
      <Typography
        variant="overline"
        align="center"
        display="block"
        sx={{ mb: 2, borderRadius: '10px' }}
      >
        Select Service(s)
      </Typography>

      {filteredServices.length === 0 ? (
        <Typography align="center" color="text.secondary">
          {isAnyAvailableSelected
            ? isAnyAvailableBlockedByPaymentCompatibility
              ? 'No services are available because card payment is required and no eligible staff can accept cards.'
              : 'No services are available for the currently eligible staff.'
            : 'No services are available for this team member.'}
        </Typography>
      ) : categoryNames.length <= 1 ? (
        <Grid2 container spacing={2}>
          {filteredServices.map((service) => (
            <Grid2 container size={12} key={service.id} justifyContent="center">
              <Paper
                onClick={() => handleServiceToggle(service)}
                elevation={0}
                sx={{
                  width: '100%',
                  cursor: 'pointer',
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  backgroundColor: isSelected(service) ? 'action.selected' : 'transparent',
                  borderLeft: isSelected(service)
                    ? `4px solid ${theme.palette.primary.main}`
                    : '4px solid transparent',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  {service.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'gray', mb: 1 }}>
                  {service.description}
                </Typography>
                <Box display="flex" justifyContent="space-between">
                  {showPrices && <Typography variant="body2">{formatServicePrice(service)}</Typography>}
                  <Typography variant="body2">{service.time} mins</Typography>
                </Box>
              </Paper>
            </Grid2>
          ))}
        </Grid2>
      ) : (
        categoryNames.map((cat, idx) => (
          categoryMap[cat] && categoryMap[cat].length > 0 ? (
          <Accordion
            key={cat}
            disableGutters
            expanded={expandedCategories.includes(cat)}
            onChange={handleAccordionChange(cat)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                {cat}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid2 container spacing={2}>
                {categoryMap[cat].map((service) => (
                  <Grid2 container size={12} key={service.id} justifyContent="center">
                    <Paper
                      onClick={() => handleServiceToggle(service)}
                      elevation={0}
                      sx={{
                        width: '100%',
                        cursor: 'pointer',
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                        backgroundColor: isSelected(service) ? 'action.selected' : 'transparent',
                        borderLeft: isSelected(service)
                          ? `4px solid ${theme.palette.primary.main}`
                          : '4px solid transparent',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'gray', mb: 1 }}>
                        {service.description}
                      </Typography>
                      <Box display="flex" justifyContent="space-between">
                        {showPrices && <Typography variant="body2">{formatServicePrice(service)}</Typography>}
                        <Typography variant="body2">{service.time} mins</Typography>
                      </Box>
                    </Paper>
                  </Grid2>
                ))}
              </Grid2>
            </AccordionDetails>
          </Accordion>
          ) : null
        ))
      )}
    </Box>
  );

};

export default ServicePicker;
